import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Box, Button, FormControlLabel, Grid, IconButton, MenuItem, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import { ErrorMessage, useFormikContext, FieldArray, Field, FieldProps } from 'formik';
import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { clearReceivingInformation, getDropDownInformation, getReceivingInformation } from '../../store/formSlice';
import { useAppDispatch } from '../../store/store';
import { timeOptions } from '../ShippingInformationComponent/ShippingInformation';
import RequiredLabel from '../UtilityComponent/RequiredLabel';
import { formatPhoneNumber } from '../UtilityComponent/validationUtils';
import './ReceivingInformation.css';
import SearchSelectComponent from '../../common/searchAndSelect/SearchSelectComponent';


interface AdditionalRecievingContact {
  alternateReceivingContactName: string;
  alternateReceivingContactEmail: string;
  alternateReceivingContactPhoneNumber: string;
}

interface receivingAddressOption {
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  locationID: string;
}



interface ReceivingInformationProps {
  onNext: () => void;
  onBack: () => void;
  values: any;
  errors: any;
  touched: any;
  receivingContactRows: any;
  onContactsChange: (contacts: AdditionalRecievingContact[]) => void;
}

export interface AdditionalRecievingContacts {
  alternateReceivingContactName: string,
  alternateReceivingContactEmail: string,
  alternateReceivingContactPhoneNumber: string
}

const columns = [
  { id: 'column1', label: '', placeholder: '' },
  { id: 'column2', label: '', placeholder: '' },
  { id: 'column3', label: '', placeholder: '' },
  { id: 'column4', label: '', placeholder: '' }
];

const styles: { [key: string]: CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    maxWidth: '80%',
    margin: 'auto',
    marginTop: '10px',
    marginBottom: '50px'
  },
  title: {
    color: 'blue',
    fontSize: '1rem'
  },
  additionalFieldsContainer: {
    width: '100%',
    marginBottom: '1rem'
  },
  additionalFieldsContainerBottom: {
    width: '74%',
    paddingBottom: '10rem',
    float: 'left'
  },
  additionalFieldsContainerRight: {
    width: '24%',
    paddingBottom: '10rem',
    float: 'right'
  },
  fieldGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '10px',
    width: '100%',
    marginTop: '20px'
  },
  fieldGroupRight: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: '10px',
    width: '20%',
    marginTop: '20px'
  },
  inputContainer: {
    flex: 1,
    marginRight: '10px',
    display: 'flex',
    flexDirection: 'column',
  },
  menuItemProp: {
    fontSize: '0.8rem !important'
  },
  customNotePep: {
    fontSize: '12px',
    color: 'red',
  },
}

const ReceivingInformation: React.FC<ReceivingInformationProps> = ({ onNext, onBack, values, errors, touched, onContactsChange }) => {
  const dispatch = useAppDispatch();
  const [additionalContactsList, setAdditionalContactsList] = useState<AdditionalRecievingContacts[]>([]);
  // const [showAddButton, setShowAddButton] = useState(true);
  const { receivingCompanyName, receivingAdditionalComments, receivingCity,
    receivingCloseTime, receivingState, receivingStreetAddress, receivingZipCode, receivingDockDoor, receivingDockHeightFlag, receivingRequiredDate, receivingRequiredTime, receivingSchedulingType, receivingContactName, receivingContactEmail, receivingContactPhoneNumber, receivingAlternateContacts, receivingLocationID
  } = useSelector(getReceivingInformation);
  const [stateOptions, setStateOptions] = useState<string[]>([]);
  const navigate = useNavigate();
  const dropDownOptions = useSelector(getDropDownInformation)
  const [showAlternateContacts, setShowAlternateContacts] = useState(false);


  useEffect(() => {
    if (!dropDownOptions) {
      navigate('/'); // Redirect to home page if state is null or undefined
    }
  }, [dropDownOptions, navigate]);

  // const [addressOptions, setAddressOptions] = useState<receivingAddressOption[]>([]);
  // const [loading, setLoading] = useState(false);
  const addressFieldRef = useRef<HTMLDivElement>(null);
  const [dropdownWidth, setDropdownWidth] = useState<number | undefined>(undefined);
  const initialValuesSet = useRef(false);
  const handleBack = () => {
    resetForm({ values }); // Reset the form state
    setErrors({}); // Clear the errors
    onBack();
  };

  useEffect(() => {
    onContactsChange(additionalContactsList);
  }, [additionalContactsList, onContactsChange]);

  // useEffect(() => {
  //   if (addressFieldRef.current) {
  //     setDropdownWidth(addressFieldRef.current.offsetWidth);
  //   }
  // }, [addressOptions]);

  useEffect(() => {
    if (dropDownOptions) {
      const { States } = dropDownOptions;

      if (States && States !== stateOptions) {

        setStateOptions(States);
      }

    }
  }, [dropDownOptions, stateOptions]);

  const handleAlternateContactsChange = (event: any, index: any) => {
    const { name, value } = event.target;

    let formattedValue = value;

    if (name === `additionalReceivingContactsList[${index}].alternateReceivingContactPhoneNumber`) {
      formattedValue = formatPhoneNumber(value, 'US');
    }

    setFieldValue(name, formattedValue);
  };

  const { setFieldValue, resetForm, setErrors } = useFormikContext();

  const setFieldIfDefined = (field: string, value: any) => {
    if (value !== undefined && !values[field]) {
      setFieldValue(field, value);
    }
  };

  useEffect(() => {
    dispatch(clearReceivingInformation());

    let dockDoor = receivingDockDoor !== undefined ? receivingDockDoor : '';

    setFieldIfDefined('receivingCompanyName', receivingCompanyName);
    setFieldIfDefined('receivingAdditionalComments', receivingAdditionalComments);
    setFieldIfDefined('receivingCity', receivingCity);
    setFieldIfDefined('receivingCloseTime', receivingCloseTime);
    setFieldIfDefined('receivingState', receivingState);
    setFieldIfDefined('receivingStreetAddress', receivingStreetAddress);
    setFieldIfDefined('receivingLocationID', receivingLocationID);
    setFieldIfDefined('receivingZipCode', receivingZipCode);
    setFieldIfDefined('receivingDockDoor', dockDoor);
    //setFieldIfDefined('receivingDockHeightFlag', dockHeightFlag);
    if (receivingDockHeightFlag !== undefined && !values.receivingDockHeightFlag) {
      setFieldIfDefined('receivingDockHeightFlag', receivingDockHeightFlag ? 'Yes' : 'No');
    }
    /*  if (receivingDockHeightFlag !== undefined) {
         
       setFieldIfDefined('receivingDockHeightFlag',receivingDockHeightFlag ? 'Yes' : 'No');
     } else {
       setFieldIfDefined('receivingDockHeightFlag','')
     } */
    setFieldIfDefined('receivingRequiredDate', receivingRequiredDate);
    setFieldIfDefined('receivingRequiredTime', receivingRequiredTime);
    setFieldIfDefined('receivingSchedulingType', receivingSchedulingType);
    setFieldIfDefined('receivingContactName', receivingContactName);
    setFieldIfDefined('receivingContactEmail', receivingContactEmail);
    setFieldIfDefined('receivingContactPhoneNumber', receivingContactPhoneNumber);
    /* if (receivingAlternateContacts && Array.isArray(receivingAlternateContacts) && receivingAlternateContacts.length > 0) {
      setShowAlternateContacts(true);
      const additionalContacts = receivingAlternateContacts.map((contact: any) => ({
        alternateReceivingContactName: contact.contactName,
        alternateReceivingContactEmail: contact.contactEmail,
        alternateReceivingContactPhoneNumber: contact.contactPhoneNumber,
      }));
      setFieldValue('additionalReceivingContactsList', additionalContacts);
    } */
    if (!initialValuesSet.current && receivingAlternateContacts && Array.isArray(receivingAlternateContacts) && receivingAlternateContacts.length > 0) {
      setShowAlternateContacts(true);
      setFieldValue('additionalReceivingContactsList', receivingAlternateContacts);
      receivingAlternateContacts.map((contact: any, index: number) => {
        setFieldValue(`additionalReceivingContactsList[${index}].alternateReceivingContactName`, contact.contactName);
        setFieldValue(`additionalReceivingContactsList[${index}].alternateReceivingContactEmail`, contact.contactEmail);
        setFieldValue(`additionalReceivingContactsList[${index}].alternateReceivingContactPhoneNumber`, contact.contactPhoneNumber);
      });
      initialValuesSet.current = true;
    }

    // Ensure showAlternateContacts is true if additionalReceivingContactsList has values
    if (values.additionalReceivingContactsList && values.additionalReceivingContactsList.length > 0) {
      setShowAlternateContacts(true);
    }
  }, [
    receivingCompanyName,
    receivingAdditionalComments,
    receivingCity,
    receivingCloseTime,
    receivingState,
    receivingStreetAddress,
    receivingZipCode,
    receivingDockDoor,
    receivingDockHeightFlag,
    receivingRequiredDate,
    receivingRequiredTime,
    receivingSchedulingType,
    receivingContactName,
    receivingContactEmail,
    receivingContactPhoneNumber,
    receivingAlternateContacts,
    receivingLocationID,
    setFieldValue,
    values,
  ]);
  const handleFormValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {

    const { name, value } = event.target;
    if (name === 'receivingContactPhoneNumber') {
      const formattedPhoneNumber = formatPhoneNumber(value, 'US');
      setFieldValue(name, formattedPhoneNumber);
    } else {
      setFieldValue(name, value);
    }

    // if (name === 'receivingStreetAddress' && value.length >= 3) {
    //   setLoading(true);
    //   dispatch(searchAddressOnType({ streetAddress: value })).then((res: any) => {
    //     setAddressOptions(res.payload.data as receivingAddressOption[]);
    //     setLoading(false);
    //   }).catch(err => {
    //     console.error('Error fetching address options:', err);
    //     setLoading(false);
    //   })
    // }
  }

  const handleAddressSelect = (selectedAddress: receivingAddressOption) => {
    setFieldValue('receivingStreetAddress', selectedAddress.streetAddress);
    setFieldValue('receivingCity', selectedAddress.city);
    setFieldValue('receivingState', selectedAddress.state);
    setFieldValue('receivingZipCode', selectedAddress.zipCode);
    setFieldValue('receivingLocationID', selectedAddress.locationID);
    // setAddressOptions([]);
  };

  const handleReceivingDockHeightFlag = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;


    setFieldValue('receivingDockHeightFlag', value); // Update the values object
  };

  const handleReceivingFlag = (event: React.ChangeEvent<HTMLInputElement>) => {
    const receivingSchedulingType = event.target.value;


    setFieldValue('receivingSchedulingType', receivingSchedulingType); // Update the values object
  };


  const getCurrentDate = () => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    // const minDate = new Date(, currentDate.getMonth(), currentDate.getDate());
    return year + "-" + month + "-" + day;
  }

  const handleAddMore = () => {
    if (!values.additionalReceivingContactsList || values.additionalReceivingContactsList.length === 0) {
      setShowAlternateContacts(true);
      const newContact = { alternateReceivingContactName: '', alternateReceivingContactEmail: '', alternateReceivingContactPhoneNumber: '' };
      setFieldValue('additionalReceivingContactsList', [newContact]);
    }
  };

  const handleRemoveContact = (index: number) => {
    const updatedContacts = values.additionalReceivingContactsList.filter((_: any, i: number) => i !== index);
    setFieldValue('additionalReceivingContactsList', updatedContacts);
    if (updatedContacts.length === 0) {
      setShowAlternateContacts(false);
    }
  };

  const addressValueChange = (name: string, value: string) => {
    setFieldValue(name, value);
  }

  return (
    <div style={{ height: '100vh', overflowY: 'auto' }}>
      <div style={styles.container}>
        <h2 style={styles.title}>Receiving Information</h2>
        <Grid container spacing={2} style={styles.additionalFieldsContainer}>
          {/* First Row: Company Name, Street Address, City */}
          <Grid item xs={12} sm={3}>
            <RequiredLabel label="Company Name" mandatory={true} />
            <TextField
              id="receivingCompanyName"
              name="receivingCompanyName"
              type="text"
              variant="outlined"
              size="small"
              fullWidth
              margin="dense"
              InputProps={{
                style: { fontSize: '0.8rem' },
              }}
              autoComplete="off"
              value={values.receivingCompanyName}
              placeholder="Please enter the Company Name"
              onChange={handleFormValueChange}
            />
            <ErrorMessage
              name="receivingCompanyName"
              component="div"
              className="error-message" // Use CSS class for consistent styling
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <SearchSelectComponent
              labelName="receivingStreetAddress"
              onValueChange={addressValueChange}
              placeHolder="Please enter the street address of receiving location"
              value={values.receivingStreetAddress}
              onSelect={handleAddressSelect}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <RequiredLabel label="City" mandatory={true} />
            <TextField
              id="receivingCity"
              name="receivingCity"
              type="text"
              variant="outlined"
              size="small"
              fullWidth
              margin="dense"
              InputProps={{
                style: { fontSize: '0.8rem' },
              }}
              autoComplete="off"
              value={values.receivingCity}
              placeholder="Please enter the City"
              onChange={handleFormValueChange}
            />
            <ErrorMessage
              name="receivingCity"
              component="div"
              className="error-message"
            />
          </Grid>

          {/* Second Row: State, Zip Code, Dock Door, Dock Height Flag */}
          <Grid item xs={12} sm={3}>
            <RequiredLabel label="State" mandatory={true} />
            <TextField
              id="receivingState"
              name="receivingState"
              select
              variant="outlined"
              size="small"
              fullWidth
              margin="dense"
              InputProps={{
                style: { fontSize: '0.8rem' },
              }}
              value={values.receivingState}
              onChange={handleFormValueChange}
            >
              {stateOptions.map((state, index) => (
                <MenuItem key={index} value={state} sx={{ fontSize: '0.8rem' }}>
                  {state}
                </MenuItem>
              ))}
            </TextField>
            <ErrorMessage
              name="receivingState"
              component="div"
              className="error-message"
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <RequiredLabel label="Zip Code" mandatory={true} />
            <TextField
              id="receivingZipCode"
              name="receivingZipCode"
              type="text"
              variant="outlined"
              size="small"
              fullWidth
              margin="dense"
              InputProps={{
                style: { fontSize: '0.8rem' },
              }}
              autoComplete="off"
              value={values.receivingZipCode}
              placeholder="Please enter the Zip Code"
              onChange={handleFormValueChange}
            />
            <ErrorMessage
              name="receivingZipCode"
              component="div"
              className="error-message"
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <RequiredLabel label="Dock Door" />
            <TextField
              id="receivingDockDoor"
              name="receivingDockDoor"
              type="text"
              variant="outlined"
              size="small"
              fullWidth
              margin="dense"
              InputProps={{
                style: { fontSize: '0.8rem' },
              }}
              autoComplete="off"
              value={values.receivingDockDoor}
              placeholder="Please enter the Dock Door"
              onChange={handleFormValueChange}
            />
            <ErrorMessage
              name="receivingDockDoor"
              component="div"
              className="error-message"
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <RequiredLabel label="Dock Height Required" mandatory={true} />
            <RadioGroup
              row
              value={values.receivingDockHeightFlag}
              onChange={handleReceivingDockHeightFlag}
              sx={{ display: 'flex', flexDirection: 'row' }}
            >
              <FormControlLabel
                value="Yes"
                control={<Radio />}
                label="Yes"
                sx={{
                  '& .MuiFormControlLabel-label': { fontSize: '0.8rem' },
                  marginTop: '4px',
                  marginLeft: '5px',
                  marginRight: '0px',
                }}
              />
              <FormControlLabel
                value="No"
                control={<Radio />}
                label="No"
                sx={{
                  '& .MuiFormControlLabel-label': { fontSize: '0.8rem' },
                  marginTop: '4px',
                  marginLeft: '5px',
                  marginRight: '0px',
                }}
              />
            </RadioGroup>
            <ErrorMessage
              name="receivingDockHeightFlag"
              component="div"
              className="error-message"
            />
          </Grid>
        </Grid>
        <h2 style={styles.title}>Contact Information at Receiving Location</h2>


        <Grid container spacing={2} style={styles.additionalFieldsContainer}>
          {/* Contact Name */}
          <Grid item xs={12} sm={4}> {/* Increased width */}
            <RequiredLabel label="Contact Name" mandatory={true} />
            <TextField
              id="receivingContactName"
              name="receivingContactName"
              type="text"
              variant="outlined"
              size="small"
              fullWidth
              margin="dense"
              InputProps={{
                style: { fontSize: '0.8rem' },
              }}
              autoComplete="off"
              value={values.receivingContactName}
              placeholder="Please enter the Contact Name"
              onChange={handleFormValueChange}
            />
            <ErrorMessage
              name="receivingContactName"
              component="div"
              className="error-message" // Reusable CSS class for consistent styling
            />
          </Grid>

          {/* Email */}
          <Grid item xs={12} sm={4}> {/* Increased width */}
            <RequiredLabel
              label="Email"
              mandatory={!values.receivingContactPhoneNumber || values.receivingContactEmail !== ''}
            />
            <TextField
              id="receivingContactEmail"
              name="receivingContactEmail"
              type="text"
              variant="outlined"
              size="small"
              fullWidth
              margin="dense"
              InputProps={{
                style: { fontSize: '0.8rem' },
              }}
              autoComplete="off"
              value={values.receivingContactEmail}
              placeholder="Please enter the Email"
              onChange={handleFormValueChange}
            />
            <ErrorMessage
              name="receivingContactEmail"
              component="div"
              className="error-message"
            />
          </Grid>

          {/* Or Label */}
          <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography
              variant="body1"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px", // Reduced font size for "or"
                whiteSpace: "nowrap",
                marginBottom: '0px',
                marginLeft: '5px',
                marginRight: '5px',
                marginTop: '35px',
                color: '#616161',
              }}
            >
              <span style={{ margin: "0 4px" }}>•••••</span>or<span style={{ margin: "0 4px" }}>•••••</span>
            </Typography>
          </Grid>

          {/* Phone Number */}
          <Grid item xs={12} sm={3}> {/* Increased width */}
            <RequiredLabel
              label="Phone Number"
              mandatory={!values.receivingContactEmail || values.receivingContactPhoneNumber !== ''}
            />
            <TextField
              id="receivingContactPhoneNumber"
              name="receivingContactPhoneNumber"
              type="text"
              variant="outlined"
              size="small"
              fullWidth
              margin="dense"
              InputProps={{
                style: { fontSize: '0.8rem' },
              }}
              autoComplete="off"
              value={values.receivingContactPhoneNumber}
              placeholder="Please enter the Phone Number"
              onChange={handleFormValueChange}
            />
            <ErrorMessage
              name="receivingContactPhoneNumber"
              component="div"
              className="error-message"
            />
          </Grid>

          {/* Second Row: Add Alternate Contact Info Button */}
          <Grid item xs={12} sm={9} />
          <Grid item xs={12} sm={3}>
            {!showAlternateContacts && (
              <Button
                type="button"
                variant="contained"
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  padding: '0',
                  color: '#0047BA',
                  fontWeight: 'normal',
                  alignItems: 'center',
                  boxShadow: 'none',
                  minWidth: 'unset',
                  marginTop: '10px',
                  fontFamily: 'Helvetica Neue',
                }}
                onClick={handleAddMore}
              >
                <AddCircleIcon fontSize="small" />
                <span
                  style={{
                    marginLeft: '5px',
                    color: '#0047BA !important',
                    fontSize: '0.8rem',
                    fontWeight: '700',
                  }}
                >
                  Add Alternate Contact Info
                </span>
              </Button>
            )}
          </Grid>
        </Grid>
        {showAlternateContacts && values.additionalReceivingContactsList?.length > 0 && (
          <>
            <h3 style={styles.title}>Alternate Contacts</h3>
            <div style={styles.additionalFieldsContainer}>
              <FieldArray name="additionalReceivingContactsList">
                {({ push, remove }) => (
                  <>
                    {values.additionalReceivingContactsList.map((contact: any, index: number) => (
                      <Grid container spacing={2} sx={{ marginBottom: '16px' }} key={index} alignItems="center">
                        {/* Contact Name */}
                        <Grid item xs={12} sm={3}>
                          <RequiredLabel label="Contact Name" mandatory={true} />
                          <Field name={`additionalReceivingContactsList[${index}].alternateReceivingContactName`}>
                            {({ field }: FieldProps) => (
                              <TextField
                                {...field}
                                type="text"
                                variant="outlined"
                                size="small"
                                fullWidth
                                margin="dense"
                                InputProps={{
                                  style: { fontSize: '0.8rem' },
                                }}
                                autoComplete="off"
                                placeholder="Please enter the Contact Name"
                                onChange={(event) => handleAlternateContactsChange(event, index)}
                              />
                            )}
                          </Field>
                          {errors.additionalReceivingContactsList &&
                            errors.additionalReceivingContactsList[index] &&
                            errors.additionalReceivingContactsList[index].alternateReceivingContactName && (
                              <div className="error-message">
                                {errors.additionalReceivingContactsList[index].alternateReceivingContactName}
                              </div>
                            )}
                        </Grid>

                        {/* Email */}
                        <Grid item xs={12} sm={4}>
                          <RequiredLabel label="Email" mandatory={true} />
                          <Field name={`additionalReceivingContactsList[${index}].alternateReceivingContactEmail`}>
                            {({ field }: FieldProps) => (
                              <TextField
                                {...field}
                                type="text"
                                variant="outlined"
                                size="small"
                                fullWidth
                                margin="dense"
                                InputProps={{
                                  style: { fontSize: '0.8rem' },
                                }}
                                autoComplete="off"
                                placeholder="Please enter the Email"
                                onChange={(event) => handleAlternateContactsChange(event, index)}
                              />
                            )}
                          </Field>
                          {errors.additionalReceivingContactsList &&
                            errors.additionalReceivingContactsList[index] &&
                            errors.additionalReceivingContactsList[index].alternateReceivingContactEmail && (
                              <div className="error-message">
                                {errors.additionalReceivingContactsList[index].alternateReceivingContactEmail}
                              </div>
                            )}
                        </Grid>

                        {/* Spacer with "or" */}
                        <Grid item xs={12} sm={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Typography
                            variant="body1"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              whiteSpace: 'nowrap',
                              marginBottom: '0px',
                              marginLeft: '5px',
                              marginRight: '5px',
                              marginTop: '35px',
                              color: '#616161',
                            }}
                          >
                            <span style={{ margin: '0 4px' }}>•••••</span>or<span style={{ margin: '0 4px' }}>•••••</span>
                          </Typography>
                        </Grid>

                        {/* Phone Number */}
                        {/* Phone Number */}
                        <Grid item xs={12} sm={3}>
                          <RequiredLabel label="Phone Number" mandatory={true} />
                          <Field name={`additionalReceivingContactsList[${index}].alternateReceivingContactPhoneNumber`}>
                            {({ field }: FieldProps) => (
                              <TextField
                                {...field}
                                type="text"
                                variant="outlined"
                                size="small"
                                fullWidth
                                margin="dense"
                                InputProps={{
                                  style: { fontSize: '0.8rem' },
                                }}
                                autoComplete="off"
                                placeholder="Please enter the Phone Number"
                                onChange={(event) => handleAlternateContactsChange(event, index)}
                              />
                            )}
                          </Field>
                          <div
                            style={{
                              minHeight: '20px', // Ensures consistent height
                              fontSize: '0.75rem',
                              color: 'red',
                              display: 'flex',
                              alignItems: 'center', // Vertically aligns error text
                            }}
                          >
                            {errors.additionalReceivingContactsList &&
                              errors.additionalReceivingContactsList[index] &&
                              errors.additionalReceivingContactsList[index].alternateReceivingContactPhoneNumber && (
                                <div className="error-message">
                                  {errors.additionalReceivingContactsList[index].alternateReceivingContactPhoneNumber}
                                </div>
                              )}
                          </div>
                        </Grid>

                        {/* Add/Remove Buttons */}
                        <Grid item xs={12} sm={1} sx={{ display: 'flex', justifyContent: 'flex-start', gap: '10px' }}>
                          {values.additionalReceivingContactsList.length < 2 && (
                            <IconButton
                              aria-label="addMore"
                              sx={{ marginTop: '4px' }}
                              onClick={() =>
                                push({
                                  alternateReceivingContactName: '',
                                  alternateReceivingContactEmail: '',
                                  alternateReceivingContactPhoneNumber: '',
                                })
                              }
                              size="small"
                            >
                              <AddCircleIcon />
                            </IconButton>
                          )}
                          <IconButton
                            aria-label="remove"
                            sx={{ marginTop: '4px' }}
                            onClick={() => handleRemoveContact(index)}
                            size="small"
                          >
                            <RemoveCircleIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ))}
                  </>
                )}
              </FieldArray>
            </div>
          </>
        )}

        <h2 style={styles.title}>Scheduling Information at Receiving Location</h2>
        <Grid container spacing={2} style={styles.additionalFieldsContainer}>
          {/* Single Row: Required Delivery Date, Required Time, Close Time, Scheduling Type */}
          <Grid item xs={12} sm={9}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Required Delivery Date" mandatory={true} />
                <TextField
                  id="receivingRequiredDate"
                  name="receivingRequiredDate"
                  type="date"
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: { fontSize: '0.8rem' },
                    inputProps: { min: getCurrentDate() },
                  }}
                  autoComplete="off"
                  value={values.receivingRequiredDate}
                  placeholder="Please enter the Required Delivery Date"
                  onChange={handleFormValueChange}
                />
                <div style={{ minHeight: '20px', fontSize: '0.75rem', color: 'red' }}>
                  <ErrorMessage name="receivingRequiredDate" component="div" className="error-message" />
                </div>
              </Grid>

              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Required Time" mandatory={true} />
                <TextField
                  id="receivingRequiredTime"
                  name="receivingRequiredTime"
                  select
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: { fontSize: '0.8rem' },
                  }}
                  autoComplete="off"
                  value={values.receivingRequiredTime}
                  placeholder="Please select the Required Time"
                  onChange={handleFormValueChange}
                >
                  {timeOptions.map((timeVal, index) => (
                    <MenuItem key={index} value={timeVal}>
                      {timeVal}
                    </MenuItem>
                  ))}
                </TextField>
                <ErrorMessage name="receivingRequiredTime" component="div" className="error-message" />
              </Grid>

              <Grid item xs={12} sm={4}>
                <RequiredLabel label="Receiving Location Close Time" mandatory={true} />
                <TextField
                  id="receivingCloseTime"
                  name="receivingCloseTime"
                  select
                  variant="outlined"
                  size="small"
                  fullWidth
                  margin="dense"
                  InputProps={{
                    style: { fontSize: '0.8rem' },
                  }}
                  autoComplete="off"
                  value={values.receivingCloseTime}
                  placeholder="Please select the Close Time"
                  onChange={handleFormValueChange}
                >
                  {timeOptions.map((timeVal, index) => (
                    <MenuItem key={index} value={timeVal}>
                      {timeVal}
                    </MenuItem>
                  ))}
                </TextField>
                <div style={{ minHeight: '20px', fontSize: '0.75rem', color: 'red' }}>
                  <ErrorMessage name="receivingCloseTime" component="div" className="error-message" />
                </div>
              </Grid>
              <Grid item xs={12} sm={12}>
                <small style={styles.customNotePep}>
                  *Please select dates carefully, keeping in mind distance and factors like transit or delivery times.
                </small>
              </Grid>
            </Grid>
            {/* Next Row: Additional Comments */}
            <Grid item xs={12} sx={{ marginTop: '16px' }}>
              <RequiredLabel label="Additional Comments" />
              <TextField
                id="receivingAdditionalComments"
                name="receivingAdditionalComments"
                type="text"
                variant="outlined"
                size="small"
                fullWidth
                margin="dense"
                InputProps={{
                  style: { fontSize: '0.8rem' },
                }}
                autoComplete="off"
                value={values.receivingAdditionalComments}
                placeholder="Please enter any additional comments"
                onChange={handleFormValueChange}
              />
              <div style={{ minHeight: '20px', fontSize: '0.75rem', color: 'red' }}>
                <ErrorMessage name="receivingAdditionalComments" component="div" className="error-message" />
              </div>
            </Grid>
          </Grid>


          <Grid item xs={12} sm={3}>
            <RequiredLabel label="Scheduling Type" mandatory={true} />
            <RadioGroup
              row
              value={values.receivingSchedulingType}
              onChange={handleReceivingFlag}
              sx={{ display: 'flex', flexDirection: 'row', gap: '8px' }}
            >
              <div
                style={{
                  padding: '5.4px',
                  marginTop: '8px',
                  border: '1px solid',
                  borderRadius: '10px',
                  borderColor: '#ada6a6',
                  paddingLeft: '20px',
                }}
              >
                <FormControlLabel
                  value="First Come, First Serve"
                  control={<Radio />}
                  label="First Come, First Serve"
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                />
                <FormControlLabel
                  value="Appointment Required"
                  control={<Radio />}
                  label="Appointment Required"
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                />
              </div>
            </RadioGroup>
            <div style={{ minHeight: '20px', fontSize: '0.75rem', color: 'red' }}>
              <ErrorMessage name="receivingSchedulingType" component="div" className="error-message" />
            </div>
          </Grid>


        </Grid>
      </div>

      <Box
        sx={{
          position: 'fixed', // Keep the buttons fixed at the bottom
          bottom: '60px', // Distance from the bottom of the screen
          left: '8rem', // Maintain position on normal screens
          width: 'calc(100% - 16rem)', // Adjust width dynamically based on left margin
          display: 'flex',
          flexDirection: 'row', // Keep buttons in a row
          justifyContent: 'space-between', // Align Back to the left and Next to the right
          alignItems: 'center',
          padding: '0', // Remove extra padding
          gap: '0', // Remove gaps between buttons
          '@media (max-width: 768px)': {
            left: '1rem', // Adjust left margin for smaller screens
            width: 'calc(100% - 2rem)', // Adjust width for smaller screens
          },
        }}
      >
        <Button
          variant="contained"
          style={{
            backgroundColor: '#F6EB39',
            color: '#2E2E38',
            fontFamily: 'Helvetica Neue',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '22px',
            textAlign: 'center',
          }}
          onClick={handleBack}
        >
          Back
        </Button>
        <Button
          variant="contained"
          style={{
            backgroundColor: '#F6EB39',
            color: '#2E2E38',
            fontFamily: 'Helvetica Neue',
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '22px',
            textAlign: 'center',
          }}
          onClick={onNext}
        >
          Next
        </Button>
      </Box>
    </div >
  );
};

export default ReceivingInformation;